import { useEffect } from "react";

export const SEO_SITE_NAME = "Free Cash Tracker";
export const SEO_SITE_URL = "https://www.freecashtracker.com";
export const SEO_DEFAULT_IMAGE = `${SEO_SITE_URL}/og-image.png`;

function toAbsoluteUrl(pathOrUrl) {
  const raw = String(pathOrUrl || "/").trim();
  if (/^https?:\/\//i.test(raw)) return raw;
  const path = raw.startsWith("/") ? raw : `/${raw}`;
  return path === "/" ? `${SEO_SITE_URL}/` : `${SEO_SITE_URL}${path}`;
}

function ensureMeta(attr, key, content) {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function ensureCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function updateJsonLd(jsonLd) {
  const scriptId = "route-jsonld";
  const existing = document.getElementById(scriptId);
  if (!jsonLd) {
    if (existing) existing.remove();
    return;
  }

  const payload = JSON.stringify(jsonLd);
  let script = existing;

  if (!script) {
    script = document.createElement("script");
    script.id = scriptId;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }

  script.textContent = payload;
}

export function usePageSeo({
  title,
  description,
  canonicalPath,
  noindex = false,
  nofollow = false,
  ogType = "website",
  ogTitle,
  ogDescription,
  ogImage = SEO_DEFAULT_IMAGE,
  twitterCard = "summary_large_image",
  jsonLd = null,
}) {
  useEffect(() => {
    const pageTitle = title || `${SEO_SITE_NAME} - Track your cash over time`;
    const pageDescription =
      description ||
      "Track your total cash over time with quick snapshots, account breakdowns, and progress insights.";
    const canonical = toAbsoluteUrl(canonicalPath || window.location.pathname);
    const robots = `${noindex ? "noindex" : "index"},${nofollow ? "nofollow" : "follow"}`;

    document.title = pageTitle;

    ensureMeta("name", "description", pageDescription);
    ensureMeta("name", "application-name", SEO_SITE_NAME);
    ensureMeta("name", "robots", robots);

    ensureMeta("property", "og:type", ogType);
    ensureMeta("property", "og:site_name", SEO_SITE_NAME);
    ensureMeta("property", "og:title", ogTitle || pageTitle);
    ensureMeta("property", "og:description", ogDescription || pageDescription);
    ensureMeta("property", "og:url", canonical);
    ensureMeta("property", "og:image", ogImage);

    ensureMeta("name", "twitter:card", twitterCard);
    ensureMeta("name", "twitter:title", ogTitle || pageTitle);
    ensureMeta("name", "twitter:description", ogDescription || pageDescription);
    ensureMeta("name", "twitter:image", ogImage);

    ensureCanonical(canonical);
    updateJsonLd(jsonLd);
  }, [
    title,
    description,
    canonicalPath,
    noindex,
    nofollow,
    ogType,
    ogTitle,
    ogDescription,
    ogImage,
    twitterCard,
    jsonLd,
  ]);
}
